<template>
  <div class="vld-parent">

    <b-row>
      <b-col md="8">
        <h4 class="font-weight-bold ml-1">Skift</h4>
        <p class="text-muted small ml-1">
          Planlegg rotasjon og bytter i
          <b-link to="/dashboard/mannskapsplan">Mannskapsplanleggeren</b-link>.
        </p>
      </b-col>

      <b-col md="1">
        <div class="float-right">
          <label class="nav-item navbar-text navbar-search-box p-0 active">
            <i class="ion ion-ios-search navbar-icon align-middle"></i>
            <span class="navbar-search-input pl-2">
              <b-form-input
                class="ml-2"
                v-model="filter"
                placeholder="Search"
              ></b-form-input>
            </span>
          </label>
        </div>
      </b-col>

      <b-col md="3">
        <b-button
          class="float-right"
          variant="primary"
          v-b-modal.newSkift
          @click="newSkiftModal()"
        >Nytt skift</b-button>
      </b-col>
    </b-row>

    <!-- Main table element -->
    <b-table
      show-empty
      caption-top
      stacked="sm"
      :items="skifts"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      empty-text="There are no skift to show"
    >
      <template v-slot:table-caption>Totalt: {{ skifts.length | numeralFormat }} skift
      </template>

      <template #cell(color)="row">
        <span
          class="skift-swatch"
          :style="{ background: row.item.color || '#dee2e6' }"
        ></span>
      </template>

      <template #cell(actions)="row">
        <b-button
          variant="primary"
          class="mr-1 float-center"
          size="sm"
          @click="editSkift(row.item)"
        >Rediger</b-button>
      </template>
    </b-table>

    <b-row>
      <b-col
        md="1"
        class="my-1"
      >
        <b-form-group class="mb-0">
          <b-form-select
            v-model="perPage"
            :options="pageOptions"
          ></b-form-select>
        </b-form-group>
      </b-col>

      <b-col
        md="2"
        class="my-1"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          class="my-0"
        ></b-pagination>
      </b-col>
    </b-row>

    <!-- Modal: New skift -->

    <b-modal
      id="newSkift"
      title="Nytt skift"
    >
      <b-form-group label="Navn">
        <b-form-input
          v-model="skift.name"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Fartøy">
        <b-form-select
          v-model="skift.vessel"
          :options="vesselSelect"
        ></b-form-select>
      </b-form-group>

      <b-form-group
        label="Farge"
        description="Brukes i Mannskapsplanleggeren"
      >
        <b-form-input
          v-model="skift.color"
          type="color"
          class="skift-color-input"
        ></b-form-input>
      </b-form-group>

      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="primary"
            class="float-right"
            @click="newSkift()"
          >Opprett skift</b-button>

          <b-button
            variant="default"
            class="float-right mr-2"
            @click="closeNewModal()"
          >Avbryt</b-button>
        </div>
      </template>
    </b-modal>

    <!-- Modal: Edit skift -->

    <b-modal
      id="editSkift"
      title="Rediger skift"
      size="lg"
    >
      <b-form-group label="Navn">
        <b-form-input
          v-model="skift.name"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Fartøy">
        <b-form-select
          v-model="skift.vessel"
          :options="vesselSelect"
        ></b-form-select>
      </b-form-group>

      <b-form-group
        label="Farge"
        description="Brukes i Mannskapsplanleggeren"
      >
        <b-form-input
          v-model="skift.color"
          type="color"
          class="skift-color-input"
        ></b-form-input>
      </b-form-group>

      <hr />

      <b-form-group label="Mannskap i dette skiftet">
        <div style="max-height: 320px; overflow-y: auto;">
          <div
            v-for="member in crews"
            :key="member.id"
            class="member-card mb-2 p-2 border rounded d-flex align-items-center"
            :class="{ 'member-card-selected': isCrewInSkift(member) }"
            @click="toggleCrewSkift(member)"
          >
            <i
              class="ion mr-2"
              :class="isCrewInSkift(member) ? 'ion-ios-checkmark-circle text-success' : 'ion-ios-radio-button-off text-muted'"
            ></i>
            <span class="flex-grow-1">
              {{ member.name }}
              <span
                v-if="member.vessel"
                class="text-muted small"
              >({{ member.vessel.name }})</span>
            </span>
            <b-badge
              pill
              :variant="roleVariant(member.role)"
            >{{ member.role || 'Ukjent' }}</b-badge>
          </div>
          <p
            v-if="crews.length === 0"
            class="text-muted small"
          >Ingen mannskap tilgjengelig</p>
        </div>
      </b-form-group>

      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="outline-danger"
            class="float-left"
            @click="deleteSkift()"
          >Slett</b-button>

          <b-button
            variant="primary"
            class="float-right"
            @click="saveSkift()"
          >Lagre</b-button>

          <b-button
            variant="default"
            class="float-right mr-2"
            @click="closeEditModal()"
          >Avbryt</b-button>
        </div>
      </template>
    </b-modal>

  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      skift: {},
      skifts: [],
      crews: [],
      vesselSelect: [],
      fields: [
        { key: "color", label: "", class: "text-center" },
        { key: "name", label: "Navn", sortable: true, class: "text-left" },
        { key: "vessel.name", label: "Fartøy", sortable: true, class: "text-left" },
        { key: "memberCount", label: "Antall mannskap", sortable: true },
        { key: "actions", label: "Handlinger" },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pageOptions: [25, 50, 100, 250, 500, 1000],
      sortBy: "name",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      isLoading: false,
      roleVariants: {
        "Skipper": "danger",
        "Styrmann": "warning",
        "Båtfører": "success",
        "Maskinist": "primary",
        "Motormann": "dark",
        "Matros": "info",
        "Dekksmann": "secondary",
        "Kokk": "warning",
        "Operatør": "primary",
        "Kadett": "secondary",
        "Lærling": "secondary",
        "Maskinpasser": "dark",
        "Matros/Kokk": "info",
        "Flåteansvarlig": "success",
      },
    };
  },
  methods: {
    roleVariant(role) {
      return this.roleVariants[role] || "light";
    },
    getSkifts() {
      this.isLoading = true;
      var company = localStorage.getItem("company");

      axios
        .get("https://api.eines-as.no/skift?_where[company]=" + company)
        .then((res) => {
          this.skifts = res.data;
          this.updateMemberCounts();
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
        });
    },
    getCrew() {
      var company = localStorage.getItem("company");

      axios
        .get("https://api.eines-as.no/crews?_where[company]=" + company)
        .then((res) => {
          this.crews = res.data;
          this.updateMemberCounts();
        });
    },
    getVessels() {
      var company = localStorage.getItem("company");

      axios
        .get("https://api.eines-as.no/vessels?_where[company]=" + company)
        .then((response) => {
          this.vesselSelect = response.data.map((vessel) => {
            return { value: vessel, text: vessel.name };
          });
        });
    },
    updateMemberCounts() {
      this.skifts.forEach((skift) => {
        skift.memberCount = this.crews.filter(
          (member) => member.skift && member.skift.id === skift.id
        ).length;
      });
    },
    isCrewInSkift(member) {
      return !!(member.skift && member.skift.id === this.skift.id);
    },
    toggleCrewSkift(member) {
      var previousSkift = member.skift;
      var newSkiftId = this.isCrewInSkift(member) ? null : this.skift.id;

      axios
        .put("https://api.eines-as.no/crews/" + member.id, { skift: newSkiftId })
        .then((response) => {
          member.skift = response.data.skift;
          this.updateMemberCounts();
        })
        .catch((error) => {
          console.log(error);
          member.skift = previousSkift;
          this.$bvToast.toast(
            "Kunne ikke oppdatere skift for " + member.name + ".",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    newSkift() {
      this.skift.company = localStorage.getItem("company");
      axios
        .post("https://api.eines-as.no/skift/", this.skift)
        .then((response) => {
          this.skifts.push(response.data);
          this.updateMemberCounts();
          this.$bvModal.hide("newSkift");
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
        });
    },
    saveSkift() {
      axios
        .put("https://api.eines-as.no/skift/" + this.skift.id, this.skift)
        .then(() => {
          this.$bvModal.hide("editSkift");
          this.getSkifts();
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
        });
    },
    deleteSkift() {
      axios
        .delete("https://api.eines-as.no/skift/" + this.skift.id)
        .then(() => {
          this.getSkifts();
          this.getCrew();
          this.$bvModal.hide("editSkift");
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
        });
    },
    editSkift(skift) {
      this.skift = skift;
      this.$bvModal.show("editSkift");
    },
    newSkiftModal() {
      this.skift = {};
    },
    closeNewModal() {
      this.$bvModal.hide("newSkift");
    },
    closeEditModal() {
      this.$bvModal.hide("editSkift");
    },
  },
  created: function () {
    this.getSkifts();
    this.getCrew();
    this.getVessels();
  },
};
</script>

<style lang="css">
.member-card {
  cursor: pointer;
  background-color: #f8f9fa;
  transition: background-color 0.15s, border-color 0.15s;
}

.member-card:hover {
  background-color: #eef1f4;
}

.skift-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.skift-color-input {
  width: 80px;
  padding: 2px;
}

.member-card-selected {
  background-color: #eaf6ec;
  border-color: #28a745 !important;
}
</style>
